import * as PropTypes from 'prop-types';
import {ReactNode} from 'react';
import DatePicker from 'react-datepicker';
import _ from 'underscore';

import {ElementKeyboardEvent} from 'util/Types';

export enum TextInputType {
  PASSWORD = 'password',
  TEXT = 'text',
}

export const TEXT_INPUT_TYPES = {
  PASSWORD: 'password',
  TEXT: 'text',
};

export enum LabelStyle {
  HIDDEN = 'hidden',
  LABEL = 'label',
  SUBLABEL = 'sublabel',
}

export const LABEL_STYLES = {
  /**
   * @note If you are running into overflow issues using hidden labels, try adding `position: relative`
   * to the label's parent. Otherwise, the label (which is applied `position: absolute` by the `sr-only`
   * class) gets positioned relative to its nearest relative-positioned ancestor (which is often a buffer tab)
   * and this can lead to buggy overflow behavior on Chrome.
   *
   * We can't easily fix this because some components accidentally rely on this behavior, like the Search
   * DateFilter components.
   *
   * See https://benchling.slack.com/archives/C08ELD8PQ/p1674499138909389 for more context.
   */
  HIDDEN: 'hidden',
  LABEL: 'label',
  SUBLABEL: 'sublabel',
};

export enum Size {
  SMALL = 'small',
  MEDIUM = 'medium',
}

// Other sizes may be added, but small and medium are the only ones currently implemented
export const SIZES = {
  SMALL: 'small',
  MEDIUM: 'medium',
};

// TODO BNCH-48321: Add a few more variants of input error message style.
export enum InputErrorMessageStyle {
  INFO = 'info',
  ERROR = 'error',
}

export interface LabelProps {
  isRequired?: boolean;
  label?: string | JSX.Element;
  labelStyle?: string;
  help?: ReactNode;
}

export const LABEL_PROPS = {
  isRequired: PropTypes.bool.isRequired,
  label: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  // Display style of the label, e.g., label or sublabel
  // eslint-disable-next-line no-restricted-syntax
  labelStyle: PropTypes.oneOf(_.values(LABEL_STYLES)).isRequired,
  help: PropTypes.node,
};

export interface InputProps extends LabelProps {
  button?: JSX.Element;
  className?: string;
  description?: string | JSX.Element;
  descriptionClassName?: string;
  error?: {
    message: ReactNode;
    style: InputErrorMessageStyle;
  } | null;
  inputClassName?: string;
  // TODO(TypeScript): Make required once defaultProps are handled correctly by TypeScript.
  size?: Size;
  isDisabled?: boolean;
}

export const INPUT = {
  ...LABEL_PROPS,
  button: (props: InputProps, ...args: [string, string, string, string]) => {
    const nodeError = PropTypes.node(props, ...args);
    if (nodeError) {
      return nodeError;
    }
    const {button, size} = props;
    if (button && button.props.size !== size) {
      return new Error('Button size and input size do not match');
    }
    return null;
  },
  // CSS class applied to the InputWrapper
  className: PropTypes.string,
  description: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  descriptionClassName: PropTypes.string,
  // CSS class applied to the actual input
  inputClassName: PropTypes.string,
  // eslint-disable-next-line no-restricted-syntax
  size: PropTypes.oneOf(_.values(SIZES)).isRequired,
};

export const DEFAULT_PROPS = {
  isRequired: false,
  labelStyle: LABEL_STYLES.LABEL,
  size: Size.SMALL,
  isDisabled: false,
};

// Legacy. Use DateInputProps or DatetimeInputProps
// TODO(Yifei): Remove this interface once DateInput has been migrated to use DatetimePicker
export interface LegacyDateAndDatetimeInputProps extends InputProps {
  autoFocus?: boolean;
  hasButton?: boolean;
  min?: string;
  max?: string;
  onKeyDown?: (e: ElementKeyboardEvent) => void;
  placeholder?: string;
  onChange?: (value: string) => void;
  datePickerPopperContainerSelector?: string;
  wrapperClassName?: string;
  value: string;
}

export type DatetimePickerRef = (node: DatePicker | null) => void;

export interface DateAndDatetimeBaseProps extends InputProps {
  autoFocus?: boolean;
  hasButton?: boolean;
  min?: string;
  max?: string;
  onKeyDown?: (e: ElementKeyboardEvent) => void;
  placeholder?: string;
  value?: string | null;
  onChange?: (value: string) => void;
  datePickerPopperContainerSelector?: string;
  wrapperClassName?: string;
  datetimePickerRef?: DatetimePickerRef;
  /**
   * If true, maintains the input text border when datePickerPopperContainerSelector is used.
   * By default, the border is removed when the date picker is rendered in a separate container.
   */
  preserveBorder?: boolean;
}
export interface DateFieldProps extends DateAndDatetimeBaseProps {
  validDatetimeInputFormats: Array<string>;
  datetimePresentationalFormat: string;
  shouldCloseOnSelect?: boolean;
}

export interface DatetimeFieldProps extends DateFieldProps {
  timeInputPlaceholder: string;
  timeInformalInputFormats: Array<string>;
  timeFormalInputFormats: Array<string>;
  timePresentationalFormat: string;
  preventDefault?: boolean;
}

export interface DateInputProps extends DateFieldProps {
  value: string;
}

export interface DatetimeInputProps extends DatetimeFieldProps {
  value: string;
}
